import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarCheck, ChevronDown, ChevronUp, ExternalLink, MessageCircle, Trash2 } from 'lucide-react';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';

type Lead = {
  id: string;
  title: string | null;
  stage: string;
  value_eur: number | null;
  contact_id: string | null;
  lost_reason: string | null;
};

export const LeadDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { orgId, loading: orgLoading, currentUser } = useOrgContext();
  const canEdit = Boolean(currentUser?.canEdit);

  const [lead, setLead] = useState<Lead | null>(null);
  const [acts, setActs] = useState<any[]>([]);
  const [note, setNote] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [showLostSection, setShowLostSection] = useState(false);

  useEffect(() => {
    if (!orgId) {
      if (!orgLoading) {
        setLead(null);
        setActs([]);
      }
      return;
    }

    let active = true;
    (async () => {
      const leadResp = await supabase
        .from('leads')
        .select('*')
        .eq('id', id)
        .eq('org_id', orgId)
        .maybeSingle();

      const activityResp = await supabase
        .from('activities')
        .select('*')
        .eq('lead_id', id)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (!active) return;

      if (!leadResp.error) {
        setLead(leadResp.data as any);
        setLostReason(leadResp.data?.lost_reason || '');
      }

      if (!activityResp.error && activityResp.data) {
        setActs(activityResp.data);
      }
    })();

    return () => {
      active = false;
    };
  }, [id, orgId, orgLoading]);

  async function changeStage(stage: string) {
    if (!canEdit) return;
    if (!orgId || !id) return;

    const payload: Record<string, any> = { stage };
    if (stage === 'lost') {
      payload.lost_reason = lostReason || lead?.lost_reason || null;
    }

    const { error } = await supabase
      .from('leads')
      .update(payload)
      .eq('id', id)
      .eq('org_id', orgId);

    if (!error && lead) {
      setLead({ ...lead, stage, lost_reason: payload.lost_reason ?? lead.lost_reason });
    }
  }

  async function addNote() {
    if (!canEdit) return;
    if (!orgId || !id) return;

    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;

    const { error } = await supabase.from('activities').insert([
      {
        contact_id: lead?.contact_id,
        lead_id: id,
        type: 'note',
        channel: 'web',
        content: note,
        created_by: uid || null,
        org_id: orgId,
      },
    ]);

    if (error) {
      alert(error.message);
      return;
    }

    setNote('');

    const refreshed = await supabase
      .from('activities')
      .select('*')
      .eq('lead_id', id)
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });

    if (!refreshed.error && refreshed.data) {
      setActs(refreshed.data);
    }
  }

  async function deleteNote(activityId: string) {
    if (!canEdit) return;
    if (!orgId || !id) return;

    if (!confirm(t('crm.detail.lead.deleteNoteConfirm'))) {
      return;
    }

    const { error } = await supabase
      .from('activities')
      .delete()
      .eq('id', activityId)
      .eq('org_id', orgId)
      .eq('lead_id', id);

    if (error) {
      alert(error.message);
      return;
    }

    // Refresh activities
    const refreshed = await supabase
      .from('activities')
      .select('*')
      .eq('lead_id', id)
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });

    if (!refreshed.error && refreshed.data) {
      setActs(refreshed.data);
    }
  }

  const timeline = useMemo(
    () =>
      acts.map((a) => ({
        id: a.id,
        type: a.type,
        channel: a.channel,
        content: a.content,
        created_at: a.created_at,
      })),
    [acts]
  );

  const formatTypeLabel = (type?: string) => {
    if (!type) return t('crm.detail.activityTypes.activity');
    const key = `crm.detail.activityTypes.${type}` as const;
    const translated = t(key);
    return translated !== key ? translated : type.replace(/_/g, ' ');
  };

  const formatChannelLabel = (channel?: string) => {
    if (!channel) return '—';
    const key = `crm.detail.activityChannels.${channel}` as const;
    const translated = t(key);
    return translated !== key ? translated : channel;
  };

  if (!lead) return <div>{t('crm.detail.lead.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> {t('crm.detail.lead.back')}
          </button>
          <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{lead.title || t('crm.detail.lead.untitledLead')}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t('crm.detail.lead.potentialValue')} {lead.value_eur ?? 0}</div>
        </div>
        <div className="flex items-center gap-2">
          {(['new', 'qualified', 'scheduled', 'won', 'lost'] as const).map((stage) => {
            const isActive = lead.stage === stage;
            return (
              <button
                key={stage}
                onClick={() => (canEdit ? changeStage(stage) : undefined)}
                disabled={!canEdit && !isActive}
                className={`px-3 py-1.5 rounded border text-xs uppercase tracking-wide transition-colors ${
                  isActive 
                    ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-700 font-semibold' 
                    : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                } ${canEdit ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : 'opacity-60 cursor-not-allowed'}`}
              >
                {t(`crm.leads.pipeline.headings.${stage}`)}
              </button>
            );
          })}
        </div>
      </div>

      {lead.stage === 'lost' && (
        <div className="p-4 border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20 rounded-lg text-sm text-rose-700 dark:text-rose-400">
          {t('crm.detail.lead.lostReason')}: {lead.lost_reason || t('crm.detail.lead.lostReasonNotDocumented')}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-md">
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium">{t('crm.detail.lead.newNote')}</div>
          <textarea
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 min-h-[90px] bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('crm.detail.lead.notesPlaceholder')}
            disabled={!canEdit}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={addNote}
              disabled={!canEdit || !note.trim()}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors shadow-md ${
                canEdit && note.trim()
                  ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('crm.detail.lead.save')}
            </button>
          </div>
        </div>
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-md">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('crm.detail.lead.dealHygiene')}</div>
            <button
              onClick={() => setShowLostSection((prev) => !prev)}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-1 transition-colors"
            >
              {showLostSection ? (
                <>
                  {t('crm.detail.lead.hide')} <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  {t('crm.detail.lead.markLost')} <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          </div>
          {showLostSection && (
            <div className="space-y-2">
              <textarea
                className="w-full border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                rows={3}
                placeholder={t('crm.detail.lead.whyLoseDeal')}
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              />
              <button
                onClick={() => changeStage('lost')}
                className="px-3 py-1.5 rounded bg-rose-500 text-white text-xs hover:bg-rose-600 transition-colors shadow-md"
              >
                {t('crm.detail.lead.saveLostReason')}
              </button>
            </div>
          )}
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <CalendarCheck className="w-3 h-3" /> {t('crm.detail.lead.lastUpdate')}: {timeline[0] ? new Date(timeline[0].created_at).toLocaleString() : '—'}
            <span className="inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              <a className="hover:text-cyan-600 dark:hover:text-cyan-400 text-cyan-600 dark:text-cyan-400 transition-colors" href="/crm/appointments">
                {t('crm.detail.lead.appointments')}
              </a>
            </span>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-md overflow-hidden">
        {timeline.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">{t('crm.detail.lead.noActivityYet')}</div>
        ) : (
          timeline.map((entry) => {
            const isWhatsapp = entry.type?.startsWith('whatsapp');
            const accentClass = entry.type === 'whatsapp_inbound'
              ? 'text-emerald-600 dark:text-emerald-400'
              : entry.type === 'whatsapp_outbound'
              ? 'text-sky-600 dark:text-sky-400'
              : entry.type === 'review_request_sent'
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-gray-700 dark:text-gray-300';
            const isNote = entry.type === 'note';
            return (
              <div key={entry.id} className="p-4 border-t border-gray-200 dark:border-gray-700 first:border-t-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group relative">
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${accentClass}`}>
                    <MessageCircle className="w-3 h-3" /> {formatTypeLabel(entry.type)}
                    <span className="text-gray-500 dark:text-gray-400 normal-case">{formatChannelLabel(entry.channel)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                    {isNote && canEdit && (
                      <button
                        onClick={() => deleteNote(entry.id)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 transition-all"
                        title={t('crm.detail.contact.deleteNoteTitle')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mt-2 leading-relaxed">
                  {isWhatsapp ? (
                    <div className={`rounded-lg border px-3 py-2 text-sm ${
                      entry.type === 'whatsapp_inbound' 
                        ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100' 
                        : 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 text-sky-900 dark:text-sky-100'
                    }`}>
                      {entry.content}
                    </div>
                  ) : entry.type === 'review_request_sent' ? (
                    <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                      <MessageCircle className="w-3 h-3" />
                      <span>{entry.content}</span>
                    </div>
                  ) : (
                    entry.content
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LeadDetailPage;



